import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Flame, Wind, Soup, AlertTriangle, FlaskConical, ShoppingCart, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";

/* COOK overlay — the shard lab bench. Transparent root; centered hazmat card with
   a big temperature arc, burner/vent/stir controls and a 90-second cook clock.
   Hold 165–180°C for purity; push past 205°C and things start going wrong. */

const fmt$ = (n) => `$${(n ?? 0).toLocaleString()}`;

function TempGauge({ temp, window: [lo, hi], exploded }) {
  const min = 0, max = 260;
  const pct = Math.max(0, Math.min(1, (temp - min) / (max - min)));
  const angle = -120 + pct * 240;
  const inBand = temp >= lo && temp <= hi;
  const color = exploded ? "#7f1d1d" : temp > 205 ? "#ef4444" : inBand ? "#67e8f9" : "#f59e0b";
  const arc = (a0, a1, r) => {
    const p = (a) => [60 + r * Math.cos((a - 90) * Math.PI / 180), 60 + r * Math.sin((a - 90) * Math.PI / 180)];
    const [x0, y0] = p(a0), [x1, y1] = p(a1);
    return `M ${x0} ${y0} A ${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1} ${y1}`;
  };
  const aLo = -120 + ((lo - min) / (max - min)) * 240;
  const aHi = -120 + ((hi - min) / (max - min)) * 240;
  return (
    <div className="relative w-40 h-40">
      <svg viewBox="0 0 120 120" className="w-full h-full">
        <path d={arc(-120, 120, 50)} fill="none" stroke="#27272a" strokeWidth="7" strokeLinecap="round" />
        <path d={arc(aLo, aHi, 50)} fill="none" stroke="#67e8f966" strokeWidth="7" strokeLinecap="round" />
        <path d={arc(-120, angle, 50)} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${color})`, transition: "stroke 0.4s" }} />
        {/* danger ticks */}
        {[205, 230].map((t) => {
          const a = (-120 + ((t - min) / (max - min)) * 240) * Math.PI / 180;
          return <line key={t} x1={60 + 42 * Math.cos(a - Math.PI / 2)} y1={60 + 42 * Math.sin(a - Math.PI / 2)}
            x2={60 + 50 * Math.cos(a - Math.PI / 2)} y2={60 + 50 * Math.sin(a - Math.PI / 2)} stroke="#ef4444" strokeWidth="2" />;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="font-mono text-2xl font-black" style={{ color, textShadow: `0 0 14px ${color}88` }}>{Math.round(temp)}°</p>
        <p className="font-mono text-[7px] uppercase tracking-[0.3em] text-zinc-500">{inBand ? "shard band" : temp > 205 ? "DANGER" : "off band"}</p>
      </div>
    </div>
  );
}

export default function CookCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [shopOpen, setShopOpen] = useState(false);
  const [exploded, setExploded] = useState(false);
  const [result, setResult] = useState(null);
  const sfx = usePhoneAudio();
  const demoCook = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const load = useCallback(async () => {
    if (demo) {
      setState((s) => s || { lab: { equipment: { hotplate: true }, ingredients: { sniffle_tabs: 2, red_powder: 2, bench_solvent: 1 } }, suspicion: 22, rep: 4,
        shop: { lab: { hotplate: 800, labkit: 2500 }, ingredients: { sniffle_tabs: 120, red_powder: 60, bench_solvent: 40 } } });
      return;
    }
    try {
      const r = await api.get(`/nc/servers/${serverId}/drugs/state`, { params: { token } });
      setState(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [demo, serverId, token]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 1600);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // demo cook simulation (mirrors the server model)
  useEffect(() => {
    if (!demo) return;
    const iv = setInterval(() => {
      const c = demoCook.current;
      if (!c || c.phase !== "cooking") return;
      c.elapsed += 0.5;
      c.temp += (c.burner ? 3.2 : -1.1) * 0.5; // mirrors server COOK_HEAT_RATE / COOK_COOL_RATE
      if (c.temp >= 165 && c.temp <= 180) c.in_window_s += 0.5;
      if (c.temp > 230) { c.phase = "boom"; setExploded(true); sfx.error?.(); }
      setState((s) => s && { ...s, cook: { ...c } });
    }, 500);
    return () => clearInterval(iv);
  }, [demo]);  // eslint-disable-line react-hooks/exhaustive-deps

  const cook = demo ? (demoCook.current ? { ...demoCook.current, window: [165, 180] } : null) : state?.cook;
  const lab = state?.lab || { equipment: {}, ingredients: {} };

  const start = async () => {
    sfx.click?.();
    setExploded(false); setResult(null);
    if (demo) {
      demoCook.current = { phase: "cooking", elapsed: 0, total: 90, temp: 20, burner: false, stirs: 0, in_window_s: 0, done: false };
      setState((s) => ({ ...s, cook: demoCook.current }));
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/drugs/cook/start`, {}, { params: { token } });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const adjust = async (action) => {
    if (busy) return;
    setBusy(action);
    sfx.keyTick();
    if (demo) {
      const c = demoCook.current;
      if (c) {
        if (action === "burner") c.burner = !c.burner;
        else if (action === "vent") c.temp -= 6;
        else { c.stirs += 1; c.temp -= 0.8; }
        setState((s) => ({ ...s, cook: { ...c, window: [165, 180] } }));
      }
      setBusy("");
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/drugs/cook/adjust`, { action }, { params: { token } });
      if (r.data.exploded) { setExploded(true); sfx.error?.(); toast.error(r.data.message); }
      setState((s) => ({ ...s, cook: r.data.cook || null }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const finish = async () => {
    sfx.click?.();
    if (demo) {
      const c = demoCook.current;
      const ratio = (c?.in_window_s || 0) / 90;
      const purity = Math.min(98, Math.round(40 + ratio * 45 + Math.min(c?.stirs || 0, 3) * 4));
      setResult({ grams: (26 * (0.4 + purity / 100 * 0.8)).toFixed(1), purity });
      demoCook.current = null;
      setState((s) => ({ ...s, cook: null }));
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/drugs/cook/finish`, {}, { params: { token } });
      setResult(r.data);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const buy = async (item) => {
    if (busy) return;
    setBusy(item);
    try {
      if (demo) { toast.success("Stocked (demo)"); }
      else {
        const r = await api.post(`/nc/servers/${serverId}/drugs/shop/buy`, { item }, { params: { token } });
        toast.success(r.data.message);
        load();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const pctDone = cook ? Math.min(100, (cook.elapsed / (cook.total || 90)) * 100) : 0;

  return (
    <div data-testid="cook-page" className="fixed inset-0 overflow-hidden pointer-events-none flex items-center justify-center p-4"
      style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif", background: "radial-gradient(ellipse 60% 70% at 50% 45%, rgba(180,60,10,0.22) 0%, rgba(30,10,4,0.08) 70%, transparent 100%)" }}>
      {demo && <PreviewRibbon what="cook bench" />}

      <div data-testid="cook-card" className="pointer-events-auto w-full max-w-md border border-amber-500/40 bg-[#140b04f2] backdrop-blur-md"
        style={{ boxShadow: "0 0 80px #f59e0b18, 0 30px 60px #000000cc" }}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-amber-500/30">
          <FlaskConical className="w-4 h-4 text-amber-400" />
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-amber-300">The bench · batch control</p>
          <p className="ml-auto font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em]">Heat {state?.suspicion ?? 0}</p>
          <button data-testid="cook-shop-toggle" onClick={() => setShopOpen((o) => !o)} className="font-mono text-[9px] uppercase tracking-[0.15em] border border-white/10 text-zinc-500 hover:text-zinc-300 px-2 py-1 transition-colors">
            <ShoppingCart className="w-3 h-3 inline" />
          </button>
        </div>

        {shopOpen ? (
          <div className="p-4 max-h-[60vh] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Bench hardware</p>
            {Object.entries(state?.shop?.lab || {}).map(([id, cost]) => (
              <div key={id} data-testid={`cook-shop-${id}`} className="flex items-center gap-3 border border-white/10 bg-black/40 px-3 py-2.5 mb-2">
                <p className="flex-1 font-mono text-[11px] text-zinc-100">{id === "hotplate" ? "Hotplate (required)" : "Lab kit (+purity, safer)"}</p>
                <button data-testid={`cook-buy-${id}`} onClick={() => buy(id)} disabled={!!state?.lab?.equipment?.[id]}
                  className="px-3 py-1.5 font-mono text-[9px] font-bold uppercase border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 disabled:opacity-30 transition-colors">
                  {state?.lab?.equipment?.[id] ? "Owned" : fmt$(cost)}
                </button>
              </div>
            ))}
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 mt-4 mb-3">Ingredients (per cook: 1 each)</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(state?.shop?.ingredients || {}).map(([id, cost]) => (
                <button key={id} data-testid={`cook-buy-${id}`} onClick={() => buy(id)}
                  className="border border-white/10 hover:border-amber-500/50 bg-black/40 px-2 py-2.5 transition-colors">
                  <p className="font-mono text-[10px] text-zinc-100 capitalize">{id}</p>
                  <p className="font-mono text-[8px] text-zinc-500">{fmt$(cost)} · have {lab.ingredients?.[id] || 0}</p>
                </button>
              ))}
            </div>
          </div>
        ) : !cook ? (
          <div className="p-6 flex flex-col items-center text-center">
            {exploded && (
              <div data-testid="cook-exploded" className="w-full border border-red-500/50 bg-red-500/10 px-4 py-3 mb-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400 flex items-center justify-center gap-2"><ShieldAlert className="w-4 h-4" /> The batch went up</p>
              </div>
            )}
            {result && (
              <div data-testid="cook-result" className="w-full border border-cyan-400/50 bg-cyan-400/10 px-4 py-3 mb-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">{result.grams}g · {result.purity}% purity</p>
              </div>
            )}
            <Soup className="w-12 h-12 text-zinc-700 mb-3" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Bench is cold</p>
            <p className="font-mono text-[9px] text-zinc-600 mb-5 leading-relaxed">One tab, one powder, one solvent per batch.<br />Hold 165–180°C for ninety seconds. Stir when it thickens.<br />Push past 205° and you're gambling with the bench.</p>
            <button data-testid="cook-start" onClick={start}
              disabled={!lab.equipment?.hotplate && !demo}
              className="px-8 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-black disabled:opacity-40"
              style={{ backgroundColor: "#f59e0b", boxShadow: "0 0 24px #f59e0b44" }}>
              Start the batch
            </button>
            {!lab.equipment?.hotplate && !demo && <p className="font-mono text-[8px] text-zinc-600 mt-2">needs a hotplate — shop, top right</p>}
          </div>
        ) : (
          <div className="p-5 flex flex-col items-center">
            {/* vessel + gauge */}
            <div className="relative flex items-center justify-center gap-6 w-full">
              <TempGauge temp={cook.temp_c ?? cook.temp ?? 20} window={cook.window || [165, 180]} exploded={exploded} />
              <div className="flex flex-col gap-2 flex-1">
                <button data-testid="cook-burner" onClick={() => adjust("burner")} disabled={!!busy}
                  className={`py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] border transition-colors flex items-center justify-center gap-2 ${cook.burner ? "border-amber-400 text-black" : "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"}`}
                  style={cook.burner ? { backgroundColor: "#f59e0b", boxShadow: "0 0 18px #f59e0b66" } : {}}>
                  <Flame className="w-4 h-4" /> Burner {cook.burner ? "on" : "off"}
                </button>
                <button data-testid="cook-vent" onClick={() => adjust("vent")} disabled={!!busy}
                  className="py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 transition-colors flex items-center justify-center gap-2">
                  <Wind className="w-4 h-4" /> Vent −6°
                </button>
                <button data-testid="cook-stir" onClick={() => adjust("stir")} disabled={!!busy}
                  className="py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] border border-white/20 text-zinc-200 hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
                  <Soup className="w-4 h-4" /> Stir ({cook.stirs || 0})
                </button>
              </div>
            </div>

            {/* cook clock */}
            <div className="w-full mt-5">
              <div className="flex justify-between font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                <span>Batch clock</span><span data-testid="cook-clock">{Math.max(0, Math.round((cook.total || 90) - (cook.elapsed || 0)))}s</span>
              </div>
              <div className="h-2 bg-black/60 border border-white/10 overflow-hidden">
                <div className="h-full transition-all duration-500" style={{ width: `${pctDone}%`, backgroundColor: "#f59e0b", boxShadow: "0 0 10px #f59e0b88" }} />
              </div>
              <div className="flex justify-between font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mt-2 mb-1">
                <span>Time in the band</span><span data-testid="cook-band-time" className="text-cyan-300">{Math.round(cook.in_window_s || 0)}s / {cook.total || 90}s</span>
              </div>
              <div className="h-2 bg-black/60 border border-white/10 overflow-hidden">
                <div className="h-full transition-all duration-500" style={{ width: `${Math.min(100, ((cook.in_window_s || 0) / (cook.total || 90)) * 100)}%`, backgroundColor: "#67e8f9", boxShadow: "0 0 10px #67e8f988" }} />
              </div>
            </div>

            {cook.done || (demo && cook.elapsed >= 90) ? (
              <button data-testid="cook-finish" onClick={finish}
                className="mt-5 w-full py-3 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-black"
                style={{ backgroundColor: "#67e8f9", boxShadow: "0 0 24px #67e8f944" }}>
                Flask it off
              </button>
            ) : (
              <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Ninety seconds. Don't walk away.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
